import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import DashboardLayout from '../../../components/dashboard/DashboardLayout';
import { Card, CardBody } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Table } from '../../../components/ui/Table';
import { Modal } from '../../../components/ui/Modal';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { FiDollarSign, FiEye } from 'react-icons/fi';
import {
  fetchAccountReceivables,
  updateAccountReceivablePayment,
  AccountReceivable,
} from '../service/accountReceivableService';
import { Timestamp } from 'firebase/firestore';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing[6]};
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.secondary[900]};
  margin: 0;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};
`;

const StatCard = styled(Card)`
  text-align: center;
`;

const StatValue = styled.div<{ color?: string }>`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${({ color }) => color || theme.colors.primary[600]};
  margin-bottom: ${theme.spacing[1]};
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  background-color: ${({ status }) => {
    switch (status) {
      case 'paid':
        return `${theme.colors.accent.success}20`;
      case 'partial':
        return `${theme.colors.accent.warning}20`;
      default:
        return `${theme.colors.primary[500]}20`;
    }
  }};
  color: ${({ status }) => {
    switch (status) {
      case 'paid':
        return theme.colors.accent.success;
      case 'partial':
        return theme.colors.accent.warning;
      default:
        return theme.colors.primary[600];
    }
  }};
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[2]};
  border: none;
  background-color: transparent;
  color: ${theme.colors.light.text.secondary};
  cursor: pointer;
  border-radius: ${theme.borderRadius.md};
  transition: all 0.2s ease;
  margin-right: ${theme.spacing[2]};

  &:hover {
    background-color: ${theme.colors.light.background.tertiary};
    color: ${theme.colors.primary[600]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AmountCell = styled.div<{ amount: number }>`
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${({ amount }) => (amount >= 0 ? theme.colors.accent.success : theme.colors.accent.danger)};
  text-align: right;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  color: ${theme.colors.light.text.secondary};
`;

const EmptyStateText = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  margin: 0;
`;

const FormGroup = styled.div`
  margin-bottom: ${theme.spacing[4]};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[4]};

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const AccountReceivablePage: React.FC = () => {
  const { user, companyData, staffData } = useAuth();
  const companyId = companyData?.id || '';

  // Check if user is a company owner/admin (logged in from companies, not staff)
  const userEmail = user?.email?.toLowerCase();
  const isCompanyOwner = !!companyData && (
    !staffData || // No staffData = pure company owner
    (userEmail && (
      companyData.email?.toLowerCase() === userEmail || 
      companyData.emails?.some((email: string) => email.toLowerCase() === userEmail)
    )) // Email matches company = company owner
  );

  // Check permissions for account receivable
  const isStaffUser = !!staffData && !isCompanyOwner;
  const canEditAccountReceivable = isCompanyOwner || (isStaffUser && staffData?.permissions?.accountReceivable === 'edit');

  const [receivables, setReceivables] = useState<AccountReceivable[]>([]);
  const [loading, setLoading] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedReceivable, setSelectedReceivable] = useState<AccountReceivable | null>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');

  useEffect(() => {
    if (companyId) {
      loadReceivables();
    }
  }, [companyId]);

  const loadReceivables = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const data = await fetchAccountReceivables(companyId);
      setReceivables(data);
    } catch (error: any) {
      console.error('Error loading receivables:', error);
      toast.error(error.message || 'Failed to load account receivables');
    } finally {
      setLoading(false);
    }
  };

  const handleRecordPayment = (receivable: AccountReceivable) => {
    setSelectedReceivable(receivable);
    setPaymentAmount('');
    setPaymentMethod('');
    setIsPaymentModalOpen(true);
  };

  const handleSubmitPayment = async () => {
    if (!companyId || !selectedReceivable || !paymentAmount) {
      toast.error('Please fill in all required fields');
      return;
    }

    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    if (amount > selectedReceivable.remainingAmount) {
      toast.error('Payment amount cannot exceed remaining amount');
      return;
    }

    setLoading(true);
    try {
      await updateAccountReceivablePayment(
        companyId,
        selectedReceivable.id,
        amount,
        paymentMethod || undefined
      );
      toast.success('Payment recorded successfully');
      setIsPaymentModalOpen(false);
      setSelectedReceivable(null);
      setPaymentAmount('');
      setPaymentMethod('');
      await loadReceivables();
    } catch (error: any) {
      console.error('Error recording payment:', error);
      toast.error(error.message || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: Date | Timestamp | undefined): string => {
    if (!date) return '—';
    let dateObj: Date;
    if (date instanceof Date) {
      dateObj = date;
    } else if ((date as any).toDate && typeof (date as any).toDate === 'function') {
      dateObj = (date as any).toDate();
    } else if ((date as any).seconds) {
      dateObj = new Date((date as any).seconds * 1000);
    } else {
      dateObj = new Date(date as any);
    }
    return dateObj.toLocaleDateString();
  };

  const totalOutstanding = receivables
    .filter(r => r.status !== 'paid')
    .reduce((sum, r) => sum + r.remainingAmount, 0);

  const totalPaid = receivables.reduce((sum, r) => sum + r.paidAmount, 0);
  const totalAmount = receivables.reduce((sum, r) => sum + r.totalAmount, 0);

  return (
    <DashboardLayout>
      <PageHeader>
        <PageTitle>Account Receivable</PageTitle>
      </PageHeader>

      <StatsContainer>
        <StatCard>
          <StatValue color={theme.colors.primary[600]}>
            PKR {totalAmount.toLocaleString()}
          </StatValue>
          <StatLabel>Total Receivables</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue color={theme.colors.accent.success}>
            PKR {totalPaid.toLocaleString()}
          </StatValue>
          <StatLabel>Total Paid</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue color={theme.colors.accent.warning}>
            PKR {totalOutstanding.toLocaleString()}
          </StatValue>
          <StatLabel>Outstanding</StatLabel>
        </StatCard>
      </StatsContainer>

      <Card>
        <CardBody>
          {loading && receivables.length === 0 ? (
            <EmptyState>
              <EmptyStateText>Loading receivables...</EmptyStateText>
            </EmptyState>
          ) : receivables.length === 0 ? (
            <EmptyState>
              <EmptyStateText>No account receivables yet.</EmptyStateText>
            </EmptyState>
          ) : (
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Invoice #</Table.HeaderCell>
                  <Table.HeaderCell>Date</Table.HeaderCell>
                  <Table.HeaderCell>Client</Table.HeaderCell>
                  <Table.HeaderCell>Client Type</Table.HeaderCell>
                  <Table.HeaderCell>Service Description</Table.HeaderCell>
                  <Table.HeaderCell style={{ textAlign: 'right' }}>Total Amount</Table.HeaderCell>
                  <Table.HeaderCell style={{ textAlign: 'right' }}>Paid Amount</Table.HeaderCell>
                  <Table.HeaderCell style={{ textAlign: 'right' }}>Remaining</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Due Date</Table.HeaderCell>
                  {canEditAccountReceivable && <Table.HeaderCell>Actions</Table.HeaderCell>}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {receivables.map((receivable) => (
                  <Table.Row key={receivable.id}>
                    <Table.Cell>
                      <strong>{receivable.invoiceNumber}</strong>
                    </Table.Cell>
                    <Table.Cell>{formatDate(receivable.date)}</Table.Cell>
                    <Table.Cell>{receivable.clientName}</Table.Cell>
                    <Table.Cell>{receivable.clientType}</Table.Cell>
                    <Table.Cell>{receivable.serviceDescription}</Table.Cell>
                    <Table.Cell style={{ textAlign: 'right' }}>
                      <AmountCell amount={receivable.totalAmount}>
                        PKR {receivable.totalAmount.toLocaleString()}
                      </AmountCell>
                    </Table.Cell>
                    <Table.Cell style={{ textAlign: 'right' }}>
                      PKR {receivable.paidAmount.toLocaleString()}
                    </Table.Cell>
                    <Table.Cell style={{ textAlign: 'right' }}>
                      <AmountCell amount={receivable.remainingAmount}>
                        PKR {receivable.remainingAmount.toLocaleString()}
                      </AmountCell>
                    </Table.Cell>
                    <Table.Cell>
                      <StatusBadge status={receivable.status}>
                        {receivable.status.charAt(0).toUpperCase() + receivable.status.slice(1)}
                      </StatusBadge>
                    </Table.Cell>
                    <Table.Cell>{formatDate(receivable.dueDate)}</Table.Cell>
                    {canEditAccountReceivable && (
                      <Table.Cell>
                        {receivable.status !== 'paid' && (
                          <ActionButton
                            onClick={() => handleRecordPayment(receivable)}
                            title="Record Payment"
                          >
                            <FiDollarSign size={18} />
                          </ActionButton>
                        )}
                      </Table.Cell>
                    )}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* Record Payment Modal */}
      <Modal
        isOpen={isPaymentModalOpen}
        onClose={() => {
          setIsPaymentModalOpen(false);
          setSelectedReceivable(null);
          setPaymentAmount('');
          setPaymentMethod('');
        }}
        title="Record Payment"
        footer={
          <>
            <Button variant="secondary" onClick={() => {
              setIsPaymentModalOpen(false);
              setSelectedReceivable(null);
              setPaymentAmount('');
              setPaymentMethod('');
            }} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleSubmitPayment} loading={loading}>
              Record Payment
            </Button>
          </>
        }
      >
        {selectedReceivable && (
          <>
            <FormGroup>
              <div style={{ 
                padding: theme.spacing[3],
                backgroundColor: theme.colors.light.background.secondary,
                borderRadius: theme.borderRadius.md,
                marginBottom: theme.spacing[4]
              }}>
                <p style={{ margin: 0, fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.secondary }}>
                  <strong>Invoice:</strong> {selectedReceivable.invoiceNumber}<br />
                  <strong>Client:</strong> {selectedReceivable.clientName}<br />
                  <strong>Total Amount:</strong> PKR {selectedReceivable.totalAmount.toLocaleString()}<br />
                  <strong>Paid Amount:</strong> PKR {selectedReceivable.paidAmount.toLocaleString()}<br />
                  <strong>Remaining:</strong> PKR {selectedReceivable.remainingAmount.toLocaleString()}
                </p>
              </div>
            </FormGroup>
            <FormGroup>
              <Input
                label="Payment Amount *"
                name="paymentAmount"
                type="text"
                value={paymentAmount}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9.]/g, '');
                  setPaymentAmount(value);
                }}
                required
                fullWidth
                dark
                placeholder="Enter payment amount"
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="Payment Method"
                name="paymentMethod"
                type="text"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                fullWidth
                dark
                placeholder="e.g., Bank Transfer, Cash, Check"
              />
            </FormGroup>
          </>
        )}
      </Modal>
    </DashboardLayout>
  );
};

export default AccountReceivablePage;

